// Auto-link glossary terms inside post HTML.
//
// Scans the (already sanitized) HTML body of an essay and replaces the first
// occurrence of each pilot glossary term with a link to its `/sozluk/{slug}`
// (TR) or `/glossary/{slug}` (EN) detail page. Links are inserted only inside
// text nodes that are outside <a>...</a> and heading (<h1>..<h6>) regions,
// so we don't nest anchors or alter section titles.
//
// Only pilot terms (which actually have detail pages) are linked — see
// `src/lib/lexicon-pilot.ts`. Linking once per term per article keeps the
// prose readable and avoids keyword stuffing.

type Lang = "tr" | "en";

type Variant = {
  id: string; // pilot TR id
  patterns: string[]; // surface forms to match
};

const TR_VARIANTS: Variant[] = [
  { id: "algoritmik-teslimiyet", patterns: ["Algoritmik Teslimiyet"] },
  { id: "ayrisma-tezi", patterns: ["Ayrışma Tezi"] },
  { id: "dikkat-madenciligi", patterns: ["Dikkat Madenciliği"] },
  { id: "epistemik-silahlanma", patterns: ["Epistemik Silahlanma"] },
  { id: "jevons-paradoksu", patterns: ["Jevons Paradoksu", "Jevons paradoksu"] },
  { id: "rebound-etkisi", patterns: ["Rebound Etkisi", "Rebound etkisi", "geri tepme etkisi"] },
  { id: "washing-ekonomisi", patterns: ["Washing Ekonomisi"] },
  { id: "yalnizlik-ekonomisi", patterns: ["Yalnızlık Ekonomisi"] },
  { id: "taahhut-borcu", patterns: ["Taahhüt Borcu", "taahhüt borcu"] },
];

// EN display label + matching enSlug, mirrored from PILOT_TERMS.
const EN_VARIANTS: { id: string; enSlug: string; patterns: string[] }[] = [
  { id: "algoritmik-teslimiyet", enSlug: "algorithmic-surrender", patterns: ["Algorithmic Surrender"] },
  { id: "ayrisma-tezi", enSlug: "decoupling-thesis", patterns: ["Decoupling Thesis"] },
  { id: "dikkat-madenciligi", enSlug: "attention-mining", patterns: ["Attention Mining"] },
  { id: "epistemik-silahlanma", enSlug: "epistemic-weaponization", patterns: ["Epistemic Weaponisation", "Epistemic Weaponization"] },
  { id: "jevons-paradoksu", enSlug: "jevons-paradox", patterns: ["Jevons Paradox", "Jevons paradox"] },
  { id: "rebound-etkisi", enSlug: "rebound-effect", patterns: ["Rebound Effect", "rebound effect"] },
  { id: "washing-ekonomisi", enSlug: "washing-economy", patterns: ["Washing Economy"] },
  { id: "yalnizlik-ekonomisi", enSlug: "loneliness-economy", patterns: ["Loneliness Economy"] },
  { id: "taahhut-borcu", enSlug: "commitment-debt", patterns: ["Commitment Debt", "commitment debt"] },
];

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

type Target = { regex: RegExp; href: string; label: string };

function buildTargets(lang: Lang): Target[] {
  if (lang === "en") {
    return EN_VARIANTS.flatMap((v) =>
      v.patterns.map((p) => ({
        regex: new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegex(p)}(?![\\p{L}\\p{N}])`, "u"),
        href: `/glossary/${v.enSlug}`,
        label: v.id,
      })),
    );
  }
  return TR_VARIANTS.flatMap((v) =>
    v.patterns.map((p) => ({
      regex: new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegex(p)}(?![\\p{L}\\p{N}])`, "iu"),
      href: `/sozluk/${v.id}`,
      label: v.id,
    })),
  );
}

function linkifyText(text: string, targets: Target[], used: Set<string>): string {
  let out = text;
  for (const t of targets) {
    if (used.has(t.label)) continue;
    const m = t.regex.exec(out);
    if (!m) continue;
    const before = out.slice(0, m.index);
    const matched = m[0];
    const after = out.slice(m.index + matched.length);
    out =
      before +
      `<a href="${t.href}" class="glossary-link" data-glossary="${t.label}">${escapeHtml(matched)}</a>` +
      after;
    used.add(t.label);
  }
  return out;
}

/**
 * Aşama 5a (editoryal karar): okurken kavram adlarından otomatik bağlantı
 * üretimi kapalı. Yalnızca editoryal olarak onaylı kavram–yazı ilişkileri
 * bağlantı üretebilir. Kod silinmedi; geri açmak için true yapın.
 */
export const AUTO_GLOSSARY_LINKS_ENABLED = false;

export function autoLinkGlossary(html: string, lang: Lang): string {
  if (!AUTO_GLOSSARY_LINKS_ENABLED) return html;
  if (!html) return html;
  const targets = buildTargets(lang);
  if (targets.length === 0) return html;
  const used = new Set<string>();

  let result = "";
  let i = 0;
  let skipDepth = 0; // inside <a> or heading

  while (i < html.length) {
    const ch = html[i];
    if (ch === "<") {
      const end = html.indexOf(">", i);
      if (end === -1) {
        result += html.slice(i);
        break;
      }
      const tag = html.slice(i, end + 1);
      const lower = tag.toLowerCase();
      if (/^<a\b/.test(lower) || /^<h[1-6]\b/.test(lower)) {
        skipDepth++;
      } else if (/^<\/a\s*>/.test(lower) || /^<\/h[1-6]\s*>/.test(lower)) {
        skipDepth = Math.max(0, skipDepth - 1);
      }
      result += tag;
      i = end + 1;
    } else {
      const next = html.indexOf("<", i);
      const text = next === -1 ? html.slice(i) : html.slice(i, next);
      result += skipDepth === 0 ? linkifyText(text, targets, used) : text;
      i = next === -1 ? html.length : next;
    }
  }
  return result;
}

/**
 * Find glossary concepts referenced in an article's HTML body.
 * Returns unique concepts (by id) in the order they first appear.
 */
export type GlossaryConcept = { id: string; label: string; href: string };

export function findGlossaryConcepts(html: string, lang: Lang): GlossaryConcept[] {
  if (!html) return [];
  const stripped = html.replace(/<[^>]+>/g, " ");
  const found = new Map<string, GlossaryConcept>();
  if (lang === "en") {
    for (const v of EN_VARIANTS) {
      for (const p of v.patterns) {
        const re = new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegex(p)}(?![\\p{L}\\p{N}])`, "u");
        if (re.test(stripped) && !found.has(v.id)) {
          found.set(v.id, { id: v.id, label: p, href: `/glossary/${v.enSlug}` });
          break;
        }
      }
    }
  } else {
    for (const v of TR_VARIANTS) {
      for (const p of v.patterns) {
        const re = new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegex(p)}(?![\\p{L}\\p{N}])`, "iu");
        if (re.test(stripped) && !found.has(v.id)) {
          found.set(v.id, { id: v.id, label: p, href: `/sozluk/${v.id}` });
          break;
        }
      }
    }
  }
  return Array.from(found.values());
}
